import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { TechProjectLogo } from "../images";

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null);
  const [scrolling, setScrolling] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimeout = useRef(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 750);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolling(window.scrollY > 10);
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 750);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const topMenus = [
    {
      label: "Solutions",
      subMenu: [
        { label: "GCC", to: "/services/gcc" },
        { label: "AI & Automation", to: "/services/ai" },
        { label: "Digital Strategy", to: "/services/dst" },
      ],
    },
    {
      label: "Products",
      subMenu: [
        { label: "PolyTXL", to: "/products/polytxl" },
        {
          label: "Nexus for Oracle Fusion",
          to: "/products/nexus-for-oracle-fusion",
          description: "Governed config promotion for Oracle Fusion",
        },
      ],
    },
    { label: "Contact", to: "/contact" },
    { label: "Blogs", to: "/blogs" },
  ];

  const sideMenus = [
    { label: "About Us", to: "/about" },
    { label: "Employees", to: "/employee" },
    { label: "Case Studies", to: "/caseStudies" },
    { label: "Careers", to: "/careers" },
    { label: "Our Clients", to: "/clients" },
    {
      label: "Industries",
      subMenu: [
        { label: "Retail", to: "/industries/rwg" },
        { label: "Healthcare", to: "/industries/hls" },
        { label: "Finance", to: "/industries/fs" },
      ],
    },
  ];

  return (
    <>
      <header
        className={`fixed left-1/2 -translate-x-1/2 z-50 w-[90%] max-w-6xl transition-all duration-300 ${
          scrolling ? "top-2" : "top-6"
        }`}
      >
        <div className="bg-[#2f378a] rounded-2xl px-8 py-4 flex items-center justify-between shadow-lg">
          <NavLink to="/">
            <img
              src={TechProjectLogo}
              alt="Logo"
              className="w-28 cursor-pointer"
            />
          </NavLink>

          {/* Top Menu */}
          <nav className="hidden [@media(min-width:750px)]:flex items-center space-x-4">
            {topMenus.map((menu) => {
              const isParentActive = menu.subMenu?.some((sub) =>
                location.pathname.startsWith(sub.to),
              );

              return !menu.subMenu ? (
                <NavLink
                  key={menu.label}
                  to={menu.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    isActive
                      ? "bg-red-500 text-white px-4 py-2 rounded-md font-medium"
                      : "text-white px-4 py-2 rounded-md hover:bg-white/10"
                  }
                >
                  {menu.label}
                </NavLink>
              ) : (
                <div
                  key={menu.label}
                  className="relative"
                  onMouseEnter={() => {
                    clearTimeout(dropdownTimeout.current);
                    setOpenDropdown(menu.label);
                  }}
                  onMouseLeave={() => {
                    dropdownTimeout.current = setTimeout(() => {
                      setOpenDropdown(null);
                    }, 250);
                  }}
                >
                  <button
                    className={`px-4 py-2 rounded-md font-medium flex items-center gap-1 ${
                      isParentActive || openDropdown === menu.label
                        ? "bg-red-500 text-white"
                        : "text-white hover:bg-white/10"
                    }`}
                  >
                    {menu.label}
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        openDropdown === menu.label ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {openDropdown === menu.label && (
                    <div
                      className="
      absolute
      top-[52px]
      left-[105px]
      -translate-x-1/2
      w-[220px]
      bg-white
      rounded-2xl
      shadow-2xl
      p-2
      z-50
      border
      border-gray-100
    "
                    >
                      {/* Submenu items */}
                      <div className="space-y-1">
                        {menu.subMenu.map((sub) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            onClick={() => {
                              setMenuOpen(false);
                              setOpenDropdown(null);
                            }}
                            className={({ isActive }) =>
                              `
            block
            rounded-xl
            px-4
            py-3
            transition-all
            duration-200
            ${isActive ? "bg-[#e8eefc]" : "hover:bg-[#f3f5fb]"}
            `
                            }
                          >
                            <div className="text-[14px] font-medium text-gray-900">
                              {sub.label}
                            </div>

                            {sub.description && (
                              <div className="mt-1 text-[12px] leading-6 text-gray-700">
                                {sub.description}
                              </div>
                            )}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="text-white">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={
                  menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"
                }
              />
            </svg>
          </button>
        </div>
      </header>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Hamburger Menu */}
      <div
        className={`fixed left-1/2 -translate-x-1/2 top-28 w-[90%] max-w-6xl z-50 transition-all duration-300 ${
          menuOpen
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="flex justify-end">
          <div className="bg-white w-60 rounded-2xl shadow-2xl p-6 space-y-4 max-h-[75vh] overflow-y-auto">
            {(isMobile ? [...topMenus, ...sideMenus] : sideMenus).map(
              (menu) => {
                const isParentActive = menu.subMenu?.some((sub) =>
                  location.pathname.startsWith(sub.to),
                );

                return !menu.subMenu ? (
                  <NavLink
                    key={menu.label}
                    to={menu.to}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      isActive
                        ? "block bg-red-500 text-white text-left px-4 py-3 rounded-lg font-medium"
                        : "block text-gray-800 text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium"
                    }
                  >
                    {menu.label}
                  </NavLink>
                ) : (
                  <div key={menu.label}>
                    <button
                      onClick={() =>
                        setOpenAccordion(
                          openAccordion === menu.label ? null : menu.label,
                        )
                      }
                      className={`w-full flex justify-between font-bold items-center py-3 px-4 rounded-lg font-medium text-left ${
                        isParentActive
                          ? "bg-red-500 text-white"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {menu.label}

                      {/* + / - icon */}
                      <span className="text-lg font-bold">
                        {openAccordion === menu.label ? "−" : "+"}
                      </span>
                    </button>

                    {openAccordion === menu.label && (
                      <div className="pl-6 space-y-2 mt-2">
                        {menu.subMenu.map((sub) => (
                          <NavLink
                            key={sub.to}
                            to={sub.to}
                            onClick={() => setMenuOpen(false)}
                            className={({ isActive }) =>
                              isActive
                                ? "block text-red-500 font-medium py-1"
                                : "block text-gray-600 hover:text-red-500 py-1"
                            }
                          >
                            {sub.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Header;
